/**
 * Starting a bulk run on the server, and then reading it — never driving it.
 *
 * THE FAULT THIS IS ABOUT
 * -----------------------
 * A batch was three HTTP requests: create, validate, execute. Each one had to contain all of its
 * work, and the work is one real render per row per sealed language. Measured on the instance that
 * reported the fault, a row costs about 1.8 s, so a hundred rows is roughly three minutes of
 * validation — and the client gives up at thirty seconds. What the operator saw was «Sunucuya
 * ulaşılamadı»; what the server did was finish the validation, mark the batch `planned`, and wait
 * for an execute nobody was left to ask for. The batch sat at «planned / 0».
 *
 * That was answered by asking for SLICES, and slices were the right answer to the timeout. They
 * were not an answer to the browser going away, because THIS MODULE WAS STILL THE DRIVER: the next
 * slice existed only because a loop here asked for it. Close the tab, lose the connection, shut the
 * laptop, and the loop stopped — with the batch half done and nothing on the server saying anybody
 * had asked for the rest.
 *
 * WHAT THIS MODULE DOES NOW
 * -------------------------
 * Two things, and neither is the work. It UPLOADS the file and ACCEPTS the run — one call that
 * persists the plan and the request together — and after that it only READS. The server's own
 * worker runs the slices, so from the moment the acceptance answers, this tab is a spectator: it
 * can be closed, refreshed or lost without stopping anything.
 *
 * AND IT STILL NEVER TURNS «I DO NOT KNOW» INTO «IT FAILED». A read that is cut off says nothing
 * about the run: the batch id is kept and the reading is retried, and when this tab truly cannot
 * keep up the outcome says so — the run is on the server, unobserved, not broken. Only the server
 * refusing is a refusal.
 *
 * NOTHING HERE CREATES A SECOND BATCH OR A SECOND JOB. The upload is keyed server-side (migration
 * 026) and so is the acceptance (migration 037): a second press, a reload and a lost answer all
 * resolve to the one that exists.
 */
import type { ApiClient } from '../../api/client';
import { isApiError } from '../../api/errors';
import { acceptBatchRun, createBatch, readBatch } from '../../api/internal';
import type { BatchOptions } from '../../api/types';

/** What the screen draws while a batch runs: the server's own counts. */
export interface BatchRunProgress {
  phase: 'creating' | 'accepting' | 'validating' | 'issuing' | 'reconciling';
  /** Rows the server has finished in this phase. */
  done: number;
  /** Rows the file has. 0 until the server has said. */
  total: number;
}

/** The answer to «did the server take this job?». */
export type BatchAcceptOutcome =
  | {
      kind: 'accepted';
      batchId: string;
      /**
       * Whether the deployment has a worker that will carry the run on. The screen may only offer
       * to close the page when this is true — a durable acceptance nothing is working off is a real
       * state, and telling somebody to shut their laptop over it would be a lie.
       */
      processing: boolean;
      /** The job already existed: this press, reload or retry did not start a second one. */
      alreadyAccepted: boolean;
    }
  | { kind: 'refused'; batchId: string | null; code: string; message: string };

/** The answer to «what happened to the job while this tab watched?». */
export type BatchFollowOutcome =
  /** Every row processed and the batch committed by the server. */
  | { kind: 'committed'; batchId: string; issued: number; failed: number; skipped: number }
  /**
   * The server stopped working this batch without committing it, and recorded why. A real, final
   * answer — not a guess by this tab.
   */
  | {
      kind: 'stopped';
      batchId: string;
      lastError: string | null;
      issued: number;
      failed: number;
      skipped: number;
    }
  /**
   * THIS TAB STOPPED WATCHING. The job is on the server and is not affected by that: the reading
   * was abandoned, not the run. Said separately from every failure shape because «tamamlanamadı»
   * is the sentence that makes somebody upload the same list a second time.
   *
   * `processing` is the server's own last word on whether anything is going to carry it. It is here
   * because the two states read the same from a tab that has stopped looking and are completely
   * different to the operator: «it is being worked, go and look at the batch» versus «it is
   * accepted and waiting for somebody to turn the worker on». A screen that collapsed them would
   * promise progress a deployment cannot make.
   */
  | {
      kind: 'unobserved';
      batchId: string;
      processed: number;
      remaining: number;
      processing: boolean;
    }
  /** The server refused to be read. `code` is its own, for a screen that wants to react to one. */
  | { kind: 'refused'; batchId: string; code: string; message: string };

export interface BatchRunHooks {
  onProgress?: (p: BatchRunProgress) => void;
}

/* ------------------------------------------------------------------ the attempt's own memory */

/**
 * WHICH ATTEMPT THIS IS — derived from the file, not from a counter.
 *
 * The memory below answers «did this attempt already open a batch?», and that question only has a
 * useful answer if the same attempt produces the same key twice. A random id does not survive a
 * refresh, and a refresh is exactly when an operator re-uploads the same list and presses again. So
 * the key is the file's own digest together with the release it is being issued from: the same
 * bytes for the same template in the same browsing session continue the batch that exists instead
 * of opening a second one.
 *
 * It is NOT a server-side guarantee and does not pretend to be one. Two things are guaranteed by
 * the server: one batch per upload key (migration 026) and one accepted run per batch (migration
 * 037). This only keeps the ordinary case — press twice, refresh, press again — from uploading the
 * same list twice before either of those can speak.
 */
export async function attemptKeyForFile(bytes: ArrayBuffer, releaseId: string): Promise<string | null> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${releaseId}:${hex}`;
  } catch {
    /* No subtle crypto (an insecure context): the run still works, it just cannot be resumed. */
    return null;
  }
}

const KEY_PREFIX = 'hpw.batch.attempt.';

/**
 * Browser storage, used for exactly one thing: «this attempt already opened batch X».
 *
 * `sessionStorage` because it belongs to this tab's visit and must not outlive it, and every access
 * is guarded: a private window, blocked site data or a thumbnail capture can make any of these
 * throw or answer empty, and a batch run must not depend on that.
 *
 * LOSING IT COSTS NOTHING THAT MATTERS, and that is deliberate: finding a run again is NOT this
 * memory's job. An accepted run is a row on the server and the operator finds it in the batch list
 * and the batch detail, by its id, after a refresh, a new tab or a new sign-in. This is only a
 * convenience that stops one tab re-uploading the same file.
 */
export function recallBatchId(attemptKey: string | null): string | null {
  if (!attemptKey) return null;
  try {
    return window.sessionStorage.getItem(KEY_PREFIX + attemptKey);
  } catch {
    return null;
  }
}

export function rememberBatchId(attemptKey: string | null, batchId: string): void {
  if (!attemptKey) return;
  try {
    window.sessionStorage.setItem(KEY_PREFIX + attemptKey, batchId);
  } catch {
    /* nothing: the run continues, it just cannot be resumed after a refresh */
  }
}

export function forgetBatchId(attemptKey: string | null): void {
  if (!attemptKey) return;
  try {
    window.sessionStorage.removeItem(KEY_PREFIX + attemptKey);
  } catch {
    /* nothing */
  }
}

/* ------------------------------------------------------------------ starting */

/** A transport failure says nothing about what the server did; a refusal does. */
const isTransportFailure = (e: unknown): boolean => isApiError(e) && e.kind === 'network';

/**
 * How many reads in a row may break before this tab stops watching.
 *
 * Not a retry of a mutation — reading a batch is safe to repeat — but a bound on how long a loop
 * keeps talking to a server it cannot reach. After this the outcome is `unobserved`, which says the
 * run is still the server's.
 */
const MAX_CONSECUTIVE_READ_FAILURES = 4;

/** How long to wait between readings, and how that grows when a reading fails. */
const POLL_MS = 1_500;
const BACKOFF_MS = [1_500, 3_000, 6_000, 10_000, 15_000];

/**
 * How long this tab will go on reading one run before it stops by itself.
 *
 * A bound rather than a policy: nothing here can stop the run, so the only thing at stake is a
 * loop that outlives any reason to exist. Generous enough that a real batch of thousands is never
 * abandoned while it is still being worked, and finite so a page left open on a run that never
 * reaches an end does not read for ever.
 */
const MAX_WATCH_MS = 2 * 60 * 60 * 1000;

/** The batch this attempt is running: the one it already opened, or a new one. */
export async function openBatch(
  api: ApiClient,
  attemptKey: string | null,
  file: { bytes: ArrayBuffer; name: string; releaseId: string },
): Promise<string> {
  const known = recallBatchId(attemptKey);
  if (known) {
    /* Trust it only if the server still has it; a stale id from an earlier database must not
       silently swallow this run. */
    try {
      const seen = await readBatch(api, known);
      return seen.batch.id;
    } catch (e) {
      if (isTransportFailure(e)) throw e;
      forgetBatchId(attemptKey);
    }
  }
  /*
   * The key goes to the SERVER, not just into this tab's memory. The memory is a convenience — it
   * is gone in a private window and it is gone if the tab is closed — and it cannot help at all
   * with the two cases that matter: an answer that never arrives, and two starts at once. Those
   * are decided by the unique index behind `Idempotency-Key` (migration 026), so asking twice
   * returns the same batch rather than making a second one over the same hundred people.
   */
  const created = await createBatch(api, file.bytes, {
    filename: file.name,
    templateReleaseId: file.releaseId,
    format: /\.csv$/i.test(file.name) ? 'csv' : 'xlsx',
    ...(attemptKey ? { idempotencyKey: attemptKey } : {}),
  });
  rememberBatchId(attemptKey, created.batch.id);
  return created.batch.id;
}

/**
 * ASK THE SERVER TO FINISH THIS BATCH — the one call that makes the run survive this tab.
 *
 * It returns as soon as the acceptance is DURABLE, which is the only moment a screen may say the
 * page can be closed. Asking twice is safe and is not a retry of the work: the acceptance is keyed
 * by the batch itself, so the second answer is the same job with `alreadyAccepted` set.
 */
export async function startServerRun(
  api: ApiClient,
  batchId: string,
  plan: { columnMapping: Record<string, string>; options: BatchOptions },
  attemptKey: string | null,
): Promise<BatchAcceptOutcome> {
  try {
    const r = await acceptBatchRun(
      api,
      batchId,
      {
        columnMapping: plan.columnMapping,
        options: plan.options,
        /*
         * ASKING AGAIN FOR A RUN THE SERVER GAVE UP ON.
         *
         * Reaching this line means an operator walked the whole wizard — template, file, mapping,
         * options — and pressed the button through its confirmation dialog, which is the explicit
         * request the server asks for. Without it a batch the server had given up on could not be
         * taken any further by anything: the acceptance refused it by name and this page does not
         * drive slices itself any more. It re-plans nothing on the server, and it cannot touch a
         * committed batch; a double press of ONE press is still one job, because that is keyed by
         * the batch and not by this flag.
         */
        resume: true,
      },
      /*
       * The product sends a key on every mutating call, so one travels here too. It is not what
       * makes this safe — the server keys the acceptance by the batch — and the key names the
       * ATTEMPT rather than the call, so a retry of the same press carries the same one.
       */
      { ...(attemptKey ? { idempotencyKey: `run:${attemptKey}` } : {}) },
    );
    return {
      kind: 'accepted',
      batchId: r.batchId,
      processing: r.processing,
      alreadyAccepted: !r.accepted,
    };
  } catch (e) {
    if (isApiError(e)) return { kind: 'refused', batchId, code: e.code, message: e.message };
    return { kind: 'refused', batchId, code: 'unexpected', message: '' };
  }
}

/* ------------------------------------------------------------------ watching */

/**
 * Read the run until the server says it is over — or until this tab gives up watching.
 *
 * The loop holds no state the server does not have, which is what makes closing the tab harmless
 * and a second viewer harmless too. `signal` lets a screen stop watching when it unmounts; that
 * ends the READING and nothing else.
 */
export async function followBatch(
  api: ApiClient,
  batchId: string,
  hooks: BatchRunHooks = {},
  opts: {
    pollMs?: number;
    maxWatchMs?: number;
    signal?: { aborted: boolean };
    sleep?: (ms: number) => Promise<void>;
    now?: () => number;
  } = {},
): Promise<BatchFollowOutcome> {
  const progress = hooks.onProgress ?? ((): void => undefined);
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => window.setTimeout(r, ms)));
  const pollMs = opts.pollMs ?? POLL_MS;
  const now = opts.now ?? ((): number => Date.now());
  const until = now() + (opts.maxWatchMs ?? MAX_WATCH_MS);
  let failures = 0;
  let last: BatchServerState | null = null;

  for (;;) {
    if (opts.signal?.aborted || now() >= until)
      return {
        kind: 'unobserved',
        batchId,
        processed: last?.processed ?? 0,
        remaining: last?.remaining ?? 0,
        /* Unknown before the first read; a tab that never got an answer must not promise one. */
        processing: last?.runProcessing ?? false,
      };
    let seen: BatchServerState | null = null;
    try {
      seen = await read(api, batchId);
      failures = 0;
    } catch (e) {
      if (!isTransportFailure(e)) {
        if (isApiError(e)) return { kind: 'refused', batchId, code: e.code, message: e.message };
        return { kind: 'refused', batchId, code: 'unexpected', message: '' };
      }
      failures += 1;
      if (failures > MAX_CONSECUTIVE_READ_FAILURES)
        return {
          kind: 'unobserved',
          batchId,
          processed: last?.processed ?? 0,
          remaining: last?.remaining ?? 0,
          processing: last?.runProcessing ?? false,
        };
      progress({ phase: 'reconciling', done: last?.processed ?? 0, total: last?.total ?? 0 });
      await sleep(BACKOFF_MS[Math.min(failures - 1, BACKOFF_MS.length - 1)] ?? pollMs);
      continue;
    }
    last = seen;
    /*
     * A DEPLOYMENT WITH NO WORKER HAS NOTHING TO WATCH.
     *
     * The acceptance is durable and a worker that starts later will take it, but nothing is going
     * to move while this page waits — so reading on would be a loop with no event to find. It is
     * reported as unobserved, which is what it is, and the screen already says what the state
     * means. Checked after the first read rather than from the acceptance's answer, so it is the
     * SERVER's current word and not one this tab is remembering.
     */
    if (!seen.runProcessing && seen.runFinishedAt === null && seen.status !== 'committed')
      return {
        kind: 'unobserved',
        batchId,
        processed: seen.processed,
        remaining: seen.remaining,
        processing: false,
      };
    /*
     * WHICH PHASE, from the rows themselves. A row that is `pending` has not been read yet, so the
     * dry run is still going; once none are, what is left is issuance. Nothing is inferred from
     * elapsed time and nothing moves unless the server's own counts moved.
     */
    progress({
      phase: seen.pending > 0 ? 'validating' : 'issuing',
      done: seen.pending > 0 ? seen.total - seen.pending : seen.issued + seen.failed + seen.skipped,
      total: seen.total,
    });
    if (seen.status === 'committed')
      return {
        kind: 'committed',
        batchId,
        issued: seen.issued,
        failed: seen.failed,
        skipped: seen.skipped,
      };
    if (seen.runFinishedAt !== null)
      return {
        kind: 'stopped',
        batchId,
        lastError: seen.runLastError,
        issued: seen.issued,
        failed: seen.failed,
        skipped: seen.skipped,
      };
    await sleep(pollMs);
  }
}

/* ------------------------------------------------------------------ reading */

/** What the server says about this batch right now. */
export interface BatchServerState {
  status: string;
  total: number;
  processed: number;
  remaining: number;
  /** Rows not yet read by the dry run: while any remain, validation is still going. */
  pending: number;
  /** Rows that produced a credential. */
  issued: number;
  /** Rows the server settled without a credential. Reported as they are, never as zero. */
  failed: number;
  skipped: number;
  /** Where the SERVER-SIDE run stands, derived by the server rather than guessed here. */
  runRequestedAt: string | null;
  runWorking: boolean;
  runFinishedAt: string | null;
  runLastError: string | null;
  /** Whether this deployment has a worker that will carry an accepted run on. */
  runProcessing: boolean;
}

async function read(api: ApiClient, batchId: string): Promise<BatchServerState> {
  const seen = await readBatch(api, batchId);
  /* The server groups the rows by status; a status it has none of is simply absent. */
  const settled = (status: string): number => (seen.summary.rowStatus as Record<string, number>)[status] ?? 0;
  return {
    status: seen.batch.status,
    total: seen.progress.total,
    processed: seen.progress.processed,
    remaining: seen.progress.remaining,
    pending: settled('pending'),
    issued: settled('issued'),
    /*
     * A committed batch may well have settled rows WITHOUT a credential — a failed row, a
     * duplicate the policy declined to issue — and `completed_with_errors` is a real outcome. So
     * these are read from the same grouping rather than reported as zero by a code path that did
     * not do the work itself.
     */
    failed: settled('failed'),
    skipped: settled('skipped_duplicate') + settled('needs_resolution'),
    /*
     * READ DEFENSIVELY, because the two halves can be one deployment apart in BOTH directions.
     * Rolling the backend back after an acceptance, or putting this page in front of an older API,
     * leaves this block absent — and a page that threw there turned a finished run into «durum
     * okunamadı». Absent is reported as «nothing known about a run», which the caller already
     * handles by sending the operator to the batch's own page; it is never dressed up as progress.
     */
    runRequestedAt: seen.run?.requestedAt ?? null,
    runWorking: seen.run?.working ?? false,
    runFinishedAt: seen.run?.finishedAt ?? null,
    runLastError: seen.run?.lastError ?? null,
    runProcessing: seen.run?.processing ?? false,
  };
}

/** The same read, for a screen that wants to reconcile without watching. `null` if it cannot ask. */
export async function reconcileBatch(api: ApiClient, batchId: string): Promise<BatchServerState | null> {
  try {
    return await read(api, batchId);
  } catch {
    return null;
  }
}
